import {
  getMatchingElementFromLists,
  getReturnsCoverageExclusions,
  itemValidForFinalSaleReturns,
  itemValidForProtection,
  itemValidForReturnsInfo,
} from "@redotech/redo-model/coverage-calculator/check-product";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  Collection,
  ExtensionShopifyClient,
  getTags,
  ShopifyProduct,
} from "@redotech/shopify-client/ajax";
import {
  getCollections,
  ShopifyExtensionClient,
} from "../client/shopify-extension-client";
import { log } from "../monitoring/log";

const STASHED_PRODUCT_INFO_KEY = "redo-product-info";

function getStashedProductInfoKey(productId: string) {
  return `${STASHED_PRODUCT_INFO_KEY}-${productId}`;
}

type productInfo = {
  tags: string[];
  collections: Collection[];
  product: ShopifyProduct | undefined;
};

type cartItemSubset = {
  product_id: string | number;
  handle: string;
  properties?: Record<string, string> | null;
};
export async function getProductInfoFromSource({
  autocheck,
  cartItem,
  product,
  shopifyClient,
  redoClient,
  signal,
}: {
  autocheck: WidgetAutocheck;
  cartItem: cartItemSubset;
  product?: ShopifyProduct;
  shopifyClient: ExtensionShopifyClient;
  redoClient: ShopifyExtensionClient;
  signal?: AbortSignal;
}): Promise<productInfo> {
  // TODO: We don't support tags & collections for Commerce Cloud yet
  if (globalThis.redoStorefront?.type === "commercecloud") {
    return { tags: [], collections: [], product: undefined };
  }

  // return cached product info if we have it
  const productInfo: productInfo | null = getLocalStorageWithExpiry(
    getStashedProductInfoKey(cartItem.product_id.toString()),
  );
  if (productInfo) {
    return productInfo;
  }

  const shopifyProduct =
    product ??
    (await safeGetShopifyProduct({ cartItem, client: shopifyClient, signal }));
  if (!shopifyProduct) {
    return { tags: [], collections: [], product: undefined };
  }

  const productTags = getTags(shopifyProduct);
  const productCollections = await getProductCollections({
    cartItem,
    shopifyProduct,
    shopifyClient,
    redoClient,
    signal,
    autocheck,
  });

  setLocalStorageWithExpiry(
    getStashedProductInfoKey(cartItem.product_id.toString()),
    {
      tags: productTags,
      collections: productCollections,
      product: shopifyProduct,
    },
  );

  return {
    tags: productTags,
    collections: productCollections,
    product: shopifyProduct,
  };
}

export async function safeGetShopifyProduct({
  cartItem,
  client,
  signal,
}: {
  cartItem: cartItemSubset;
  client: ExtensionShopifyClient;
  signal?: AbortSignal;
}) {
  try {
    return await client.productGet(cartItem.handle, signal);
  } catch (e) {
    // For BigCommerce, if a product is set to is_visible: false (e.g. a GWP product which the merchant
    // doesn't want to be shown, but is purchaseable), we will not be able to retrieve the product via
    // storefront API. However, that means invisible products will usually always be treated as eligible.
    // If we need to support tag-filtering for invisible products, we will need to create an endpoint on
    // our Embed API to retrieve the product tags.
    if (globalThis.redoStorefront?.type === "bigcommerce") {
      log(
        `Error getting product tags for ${cartItem.handle} - product is possibly not visible. Assuming no tags.`,
        e,
        cartItem.handle,
      );
      return undefined;
    } else {
      throw e;
    }
  }
}

export async function getProductCollections({
  cartItem,
  shopifyProduct,
  shopifyClient,
  redoClient,
  signal,
  autocheck,
}: {
  cartItem: cartItemSubset;
  shopifyProduct: ShopifyProduct;
  shopifyClient: ExtensionShopifyClient;
  redoClient: ShopifyExtensionClient;
  signal?: AbortSignal;
  autocheck: WidgetAutocheck;
}) {
  if (
    autocheck.coverageExcludeCollections.length ||
    autocheck.finalSaleReturns.coverageValidCollections.length
  ) {
    if (globalThis.redoStorefront?.type === "bigcommerce") {
      return (
        (await shopifyClient.getCollections(shopifyProduct?.id?.toString())) ??
        []
      );
    } else {
      return (
        (await getCollections(redoClient, {
          signal,
          productId: String(cartItem.product_id),
        })) ?? []
      );
    }
  }

  return [];
}

export async function itemValidForReturnsFrontend({
  autocheck,
  item,
  redoShopifyExtensionClient,
  shopifyClient,
  signal,
  invalidReturnProductTags,
  invalidProtectionProductTags,
  shopifyProduct,
}: {
  autocheck: WidgetAutocheck;
  item: cartItemSubset;
  redoShopifyExtensionClient: ShopifyExtensionClient;
  shopifyClient: ExtensionShopifyClient;
  signal?: AbortSignal;
  invalidReturnProductTags: string[];
  invalidProtectionProductTags?: string[];
  shopifyProduct?: ShopifyProduct;
}): Promise<{
  returnsValid: boolean;
  protectionValid: boolean;
  finalSaleReturnsValid: boolean;
}> {
  const returnStorageValue = await getLocalStorageWithExpiry(
    `redo-return-valid-excl-props-${item.product_id}`,
  );
  const protectionStorageValue = await getLocalStorageWithExpiry(
    `redo-protection-valid-${item.product_id}`,
  );
  const finalSaleStorageValue = await getLocalStorageWithExpiry(
    `redo-final-sale-valid-${item.product_id}`,
  );

  const properties: string[] = item.properties
    ? Object.keys(item.properties).reduce((acc, key) => {
        const value = item.properties?.[key];
        acc.push(key);
        if (value !== null && value !== "") {
          acc.push(`${key}: ${value}`);
        }
        return acc;
      }, [] as string[])
    : [];

  const hasValidReturnProductProperties = !getMatchingElementFromLists(
    properties,
    autocheck.coverageExcludeProperties,
  );

  if (
    returnStorageValue !== null &&
    protectionStorageValue !== null &&
    finalSaleStorageValue !== null
  ) {
    return {
      returnsValid:
        returnStorageValue === "true" && hasValidReturnProductProperties,
      protectionValid: protectionStorageValue === "true",
      finalSaleReturnsValid: finalSaleStorageValue === "true",
    };
  }

  const { tags: productTags, collections } = await getProductInfoFromSource({
    autocheck,
    cartItem: item,
    product: shopifyProduct,
    shopifyClient,
    redoClient: redoShopifyExtensionClient,
    signal,
  });

  const coverageExclusions = getReturnsCoverageExclusions({
    returnsCoverage: {
      coverageExcludeProperties: autocheck.coverageExcludeProperties,
      coverageExcludeCollections: autocheck.coverageExcludeCollections,
      invalidProductTags: invalidReturnProductTags,
    },
    finalSaleReturnsCoverage: {
      coverageEnabled: autocheck.finalSaleReturns.coverageEnabled,
      validProductTags: autocheck.finalSaleReturns.coverageValidTags,
      validCollectionTitles:
        autocheck.finalSaleReturns.coverageValidCollectionTitles ?? [],
    },
  });

  const hasValidReturnProductIgnoringProperties =
    itemValidForReturnsInfo({
      ...coverageExclusions,
      itemInfo: {
        properties: [], // We are going to cache this result in local storage, so don't take into account the properties since they change with each item added
        productTags,
        collections: collections.map((c) => c.title),
      },
    }).isEligible && autocheck.coverageEnabled;

  const hasValidProtectionProduct =
    itemValidForProtection({
      invalidProductTags: invalidProtectionProductTags || [],
      itemInfo: {
        properties,
        productTags,
        collections: collections.map((c) => c.title),
      },
    }) && autocheck.packageProtection.coverageEnabled;

  const hasValidFinalSaleProduct =
    itemValidForFinalSaleReturns({
      validProductTags: autocheck.finalSaleReturns.coverageValidTags,
      validCollections: autocheck.finalSaleReturns.coverageValidCollections,
      itemInfo: { productTags, collections: collections.map((c) => c.handle) },
      allEligible: autocheck.finalSaleReturns.allEligible || false,
    }) && autocheck.finalSaleReturns.coverageEnabled;

  setLocalStorageWithExpiry(
    `redo-return-valid-excl-props-${item.product_id}`,
    String(hasValidReturnProductIgnoringProperties),
  );

  setLocalStorageWithExpiry(
    `redo-protection-valid-${item.product_id}`,
    String(hasValidProtectionProduct),
  );

  setLocalStorageWithExpiry(
    `redo-final-sale-valid-${item.product_id}`,
    String(hasValidFinalSaleProduct),
  );

  return {
    returnsValid:
      hasValidReturnProductIgnoringProperties &&
      hasValidReturnProductProperties,
    protectionValid: hasValidProtectionProduct,
    finalSaleReturnsValid: hasValidFinalSaleProduct,
  };
}
